import { useLoaderData, useNavigate } from "react-router-dom";
import { Profile } from "../../services/profile/profileModel";
import { Fragment, useMemo, useState } from "react";
import Dialog from "../../components/Dialog";
import { createProfileRequest } from "../../services/profile/profileApi";
import ProfileCard from "./profile/ProfileCardComponent";
import { compareBoolean, orderByField } from "../../react-helpers/array";
import { User } from "../../services/user/userModel";
import { useTranslation } from "react-i18next";
import ProfileForm from "./profile/ProfileForm";
import { userService } from "../../services/user/userService";
import { loggerBuilder } from "../../services/logger";
import { toastsWithIntl } from "../../services/toastService";

const AddProfileBtn = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <button className="card-new" onClick={onClick}>
      <div className="card_image_container">
        <canvas className="card_image_bg" width="16" height="9" />
      </div>

      <div className="card_body">
        {t("candidate:dashboard.CREATE_PROFILE_BUTTON")}
      </div>
    </button>
  );
};

const CandidateDashboardPage = () => {
  const { t } = useTranslation();
  const { profiles, loggedUser } = useLoaderData() as {
    profiles: Profile[];
    loggedUser: User;
  };
  const navigate = useNavigate();
  const { getPremiumUserLink } = userService();
  const { toastError } = toastsWithIntl(["common"]);
  const logger = loggerBuilder("CandidateDashboardPage");

  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [loadingPremium, setLoadingPremium] = useState(false);

  const sortedProfiles = useMemo(
    () => profiles.sort(orderByField("featured", true, compareBoolean)),
    [profiles],
  );

  const profilesSlots = useMemo(
    () => [...Array(4)].map((_, idx) => sortedProfiles[idx] ?? null),
    [sortedProfiles],
  );

  const hasSubscription = useMemo(() => {
    return (
      loggedUser.subscriptionEndDate &&
      new Date(loggedUser.subscriptionEndDate) > new Date() &&
      loggedUser.subscriptionStartDate !== null
    );
  }, [loggedUser]);

  return (
    <div className="layout_content --bg--candidate">
      <div className="section --bg--candidate">
        <div className="container --wrap--s ">
          <h2 className="section_title">
            {profilesSlots[0] ? t("candidate:dashboard.MAIN_PROFILE") : ""}
          </h2>
          {profilesSlots[0] && (
            <p className="body">
              {t("candidate:dashboard.CREATED_AT", {
                date: new Date(profilesSlots[0].createdAt),
              })}
            </p>
          )}

          <div className="lblock">
            <div>
              {profilesSlots[0] ? (
                <ProfileCard profile={profilesSlots[0]} />
              ) : (
                <AddProfileBtn onClick={() => setShowCreateProfile(true)} />
              )}
            </div>
          </div>
        </div>
      </div>

      {hasSubscription && (
        <div className="section --bg--premium">
          <div className="container --wrap">
            <h2 className="page_title">
              {t("candidate:dashboard.premium.TITLE")}⚡
            </h2>
            <p className="body">
              {t("candidate:dashboard.premium.DESCRIPTION")}
            </p>
            <div className="lblock grid --fill">
              {profilesSlots.slice(1).map((profile, idx) => (
                <Fragment
                  key={profile ? `profile-${profile.id}` : `add-profile-${idx}`}
                >
                  {profile ? (
                    <ProfileCard profile={profile} />
                  ) : (
                    <AddProfileBtn onClick={() => setShowCreateProfile(true)} />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {!hasSubscription && (
        <div className="section --bg--premium">
          <div className="container --wrap --txt--center">
            <h2 className="page_title">
              {t("candidate:dashboard.premium.test.TITLE")}⚡
            </h2>
            <p
              className="body"
              dangerouslySetInnerHTML={{
                __html: t("candidate:dashboard.premium.test.DESCRIPTION"),
              }}
            ></p>
            <div className="lblock --txt--center">
              <a
                className="btn--white"
                href="https://www.2minutesmax.com/premium-candidats"
                target="_blank"
                rel="noreferrer"
              >
                {t("candidate:dashboard.premium.test.BUTTON_MORE")}
              </a>
              &nbsp;&nbsp;
              <button
                className="btn--1"
                disabled={loadingPremium}
                onClick={() => {
                  const handlePremiumClick = async () => {
                    try {
                      setLoadingPremium(true);
                      const result = await getPremiumUserLink(loggedUser.id);
                      if (result?.url) window.location.href = result.url;
                    } catch (err) {
                      logger.error(
                        "Erreur lors de la récupération du lien premium",
                        err,
                      );
                      toastError("common:error.header.PREMIUM_LINK_ERROR");
                      return;
                    } finally {
                      setLoadingPremium(false);
                    }
                  };

                  void handlePremiumClick();
                }}
              >
                {loadingPremium
                  ? "..."
                  : t("candidate:dashboard.premium.test.BUTTON_TRY")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateProfile && (
        <Dialog
          className="--branded"
          onClose={() => setShowCreateProfile(false)}
        >
          <div>
            <div className="popup_head">
              <h2 className="popup_title">
                {t("candidate:create-profile.DIALOG_TITLE")}
              </h2>
            </div>

            <ProfileForm
              onSubmit={(values) => {
                return createProfileRequest(values).then((profile) => {
                  return navigate(`/candidate/profiles/${profile.id}`);
                });
              }}
              onCancel={() => setShowCreateProfile(false)}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default CandidateDashboardPage;
